import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { SaveButton } from '@components/button/save-button/save-button/save-button';
import { MsfInput } from '@components/ui/forms/input/input';
import { TextareaComponent } from '@components/textarea/textarea.component';
import { NotificationTemplateDto, UserNotificationsApiService } from '@app/services/user-notifications-api.service';
import {
  NOTIFICATION_LIST_ITEM_FIELDS,
  NOTIFICATION_TEMPLATE_FIELD_DESCRIPTION_KEYS,
  NOTIFICATION_TEMPLATE_MODEL_BY_EVENT,
  NOTIFICATION_TEMPLATE_MODEL_BY_NAME,
  NotificationTemplateFieldEvent,
} from '@app/pages/admin/notification-template-fields.util';

@Component({
  selector: 'app-notification-template-fields',
  standalone: true,
  imports: [TranslateModule, FormsModule, DialogModule, Tab, TabList, TabPanel, TabPanels, Tabs, SecondaryButton, SaveButton, MsfInput, TextareaComponent],
  templateUrl: './notification-template-fields.html',
  styleUrl: './notification-template-fields.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationTemplateFieldsComponent {
  private readonly api = inject(UserNotificationsApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly languages = ['en-US', 'ru-RU', 'uz-Latn-UZ'] as const;
  readonly events = input<NotificationTemplateFieldEvent[]>([]);
  readonly templates = input<NotificationTemplateDto[]>([]);
  readonly hideWhenEmpty = input(false);
  readonly showTemplateList = input(false);
  /** Render only the edit dialog. The process-card table owns the row list. */
  readonly dialogOnly = input(false);
  /** Template id opened by a parent table. Null keeps the local button state. */
  readonly openedTemplateId = input<string | null>(null);
  readonly templateClosed = output<void>();
  readonly templateSaved = output<void>();
  readonly expandedTemplateId = signal<string | null>(null);
  readonly draftLanguage = signal<(typeof this.languages)[number]>('en-US');
  readonly draftTitles = signal<Record<string, string>>({});
  readonly draftBodies = signal<Record<string, string>>({});
  readonly saving = signal(false);
  readonly saveFailed = signal(false);
  private readonly draftSourceId = signal<string | null>(null);

  readonly activeTitle = computed(() => this.draftTitles()[this.draftLanguage()] ?? '');
  readonly activeBody = computed(() => this.draftBodies()[this.draftLanguage()] ?? '');

  constructor() {
    effect(() => {
      const id = this.openedTemplate()?.template.id ?? null;
      const source = this.templates().find(row => row.id === id) ?? null;
      if (!source) {
        return;
      }
      if (source.id === this.draftSourceId()) {
        return;
      }
      untracked(() => {
        this.draftSourceId.set(source.id);
        this.saveFailed.set(false);
        this.draftLanguage.set('en-US');
        this.draftTitles.set({
          'en-US': source.title['en-US'] ?? '',
          'ru-RU': source.title['ru-RU'] ?? '',
          'uz-Latn-UZ': source.title['uz-Latn-UZ'] ?? '',
        });
        this.draftBodies.set({
          'en-US': source.body['en-US'] ?? '',
          'ru-RU': source.body['ru-RU'] ?? '',
          'uz-Latn-UZ': source.body['uz-Latn-UZ'] ?? '',
        });
      });
    });
  }

  private readonly activeTemplateId = computed(() => this.openedTemplateId() ?? this.expandedTemplateId());

  readonly openedTemplate = computed(() => {
    const id = this.activeTemplateId();
    if (!id) {
      return null;
    }
    for (const event of this.events()) {
      const template = event.templates.find(item => item.id === id);
      if (template) {
        return { template, event };
      }
    }
    return null;
  });

  readonly openedModel = computed(() => {
    const opened = this.openedTemplate();
    const byName = opened ? NOTIFICATION_TEMPLATE_MODEL_BY_NAME[opened.template.name] : undefined;
    if (byName) {
      return byName;
    }
    const key = opened?.event.key?.toLowerCase();
    return key ? NOTIFICATION_TEMPLATE_MODEL_BY_EVENT[key] ?? null : null;
  });

  readonly openedVariables = computed(() => {
    const opened = this.openedTemplate();
    if (!opened) {
      return [];
    }
    const named = NOTIFICATION_TEMPLATE_MODEL_BY_NAME[opened.template.name];
    const modelLists = named?.lists ?? [];
    const modelScalars = named?.scalars ?? [];
    const names = [
      ...opened.event.tokens.map(token => token.replace(/[{}]/g, '')),
      ...opened.event.lists.flatMap(list => list.tokens.map(token => token.replace(/[{}]/g, ''))),
      ...modelScalars,
      ...modelLists,
      ...modelLists.flatMap(list => [...(NOTIFICATION_LIST_ITEM_FIELDS[list] ?? [])]),
    ];
    return [...new Set(names.filter(Boolean))].map(name => ({
      token: `{{${name}}}`,
      descriptionKey: NOTIFICATION_TEMPLATE_FIELD_DESCRIPTION_KEYS[name] ?? 'notificationField.unknown',
    }));
  });

  closeTemplate(): void {
    this.draftSourceId.set(null);
    this.expandedTemplateId.set(null);
    this.templateClosed.emit();
  }

  setDraftLanguage(language: string | number | undefined): void {
    if (language === 'en-US' || language === 'ru-RU' || language === 'uz-Latn-UZ') {
      this.draftLanguage.set(language);
    }
  }

  setActiveTitle(value: string): void {
    const language = this.draftLanguage();
    this.draftTitles.update(current => ({ ...current, [language]: value }));
  }

  setActiveBody(value: string): void {
    const language = this.draftLanguage();
    this.draftBodies.update(current => ({ ...current, [language]: value }));
  }

  saveTemplate(): void {
    const opened = this.openedTemplate();
    const source = this.templates().find(row => row.id === opened?.template.id);
    if (!source || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.saveFailed.set(false);
    this.api.updateTemplate(source.id, {
      name: source.name,
      title: { ...source.title, ...this.draftTitles() },
      body: { ...source.body, ...this.draftBodies() },
      includeAttachments: source.includeAttachments,
      includeReportPreview: source.includeReportPreview,
      deepLinkPattern: source.deepLinkPattern,
      isRetired: source.isRetired,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.templateSaved.emit();
        this.closeTemplate();
      },
      error: () => {
        this.saving.set(false);
        this.saveFailed.set(true);
      },
    });
  }
}
